/** Anchor type keys persisted in engine_schedules. */

export const ANCHOR_TYPE = {
  participantStartDate: 'participant_start_date',
  participantLogin: 'participant_login',
  icfSignatureDate: 'icf_signature_date',
  fixedCalendarDate: 'fixed_calendar_date',
  arm: 'arm',
  phase: 'phase',
  visitCycle: 'visit_cycle',
  visit: 'visit',
  track: 'track',
  chapter: 'chapter',
  instrument: 'instrument',
}

export const ANCHOR_GROUP = {
  singlePoint: 'single_point',
  entity: 'entity',
}

export const START_OR_END = {
  start: 'start',
  end: 'end',
}

export const VISIT_STATUS = {
  targetDate: 'target_date',
  appointmentScheduledDate: 'appointment_scheduled_date',
  missedDate: 'missed_date',
  completedDate: 'completed_date',
}

export const INSTRUMENT_EVENT = {
  available: 'available',
  completion: 'completion',
  missed: 'missed',
}

export const FIXED_DATE_MODE = {
  day: 'day',
  dayOfYear: 'day_of_year',
  weekOfYear: 'week_of_year',
}

export const RESOLVE_TO = {
  earliestOf: 'earliest_of',
  latestOf: 'latest_of',
}

export const OFFSET_DIRECTION = {
  after: 'after',
  before: 'before',
}

export const OFFSET_UNIT = {
  weeks: 'weeks',
  days: 'days',
  hours: 'hours',
  minutes: 'minutes',
}

export const OFFSET_UNIT_OPTIONS = [
  { key: OFFSET_UNIT.weeks, text: 'weeks' },
  { key: OFFSET_UNIT.days, text: 'days' },
  { key: OFFSET_UNIT.hours, text: 'hours' },
  { key: OFFSET_UNIT.minutes, text: 'minutes' },
]

export const SINGLE_POINT_ANCHOR_TYPES = [
  ANCHOR_TYPE.participantStartDate,
  ANCHOR_TYPE.participantLogin,
  ANCHOR_TYPE.icfSignatureDate,
  ANCHOR_TYPE.fixedCalendarDate,
]

export const ENTITY_ANCHOR_TYPES = [
  ANCHOR_TYPE.arm,
  ANCHOR_TYPE.phase,
  ANCHOR_TYPE.visitCycle,
  ANCHOR_TYPE.visit,
  ANCHOR_TYPE.track,
  ANCHOR_TYPE.chapter,
  ANCHOR_TYPE.instrument,
]

/** End dates are not known in advance for these types — offsets are After-only (both start and end). */
export const AFTER_ONLY_OFFSET_ANCHOR_TYPES = [
  ANCHOR_TYPE.arm,
  ANCHOR_TYPE.visitCycle,
  ANCHOR_TYPE.track,
  ANCHOR_TYPE.chapter,
]

/** @deprecated use AFTER_ONLY_OFFSET_ANCHOR_TYPES */
export const END_NO_NEGATIVE_TYPES = AFTER_ONLY_OFFSET_ANCHOR_TYPES

/** These types auto-populate Arm/Phase targeting (Phase 2). Chapter/Track do not pin. */
export const PINNING_ANCHOR_TYPES = [
  ANCHOR_TYPE.arm,
  ANCHOR_TYPE.phase,
  ANCHOR_TYPE.visitCycle,
  ANCHOR_TYPE.visit,
]

export const ANCHOR_LABELS = {
  [ANCHOR_TYPE.participantStartDate]: 'Participant start date',
  [ANCHOR_TYPE.participantLogin]: 'Participant first login',
  [ANCHOR_TYPE.icfSignatureDate]: 'ICF signature date',
  [ANCHOR_TYPE.fixedCalendarDate]: 'By exact date',
  [ANCHOR_TYPE.arm]: 'Arm',
  [ANCHOR_TYPE.phase]: 'Phase / period / epoch',
  [ANCHOR_TYPE.visitCycle]: 'Visit cycle (treatment cycle)',
  [ANCHOR_TYPE.visit]: 'Visit',
  [ANCHOR_TYPE.track]: 'Track',
  [ANCHOR_TYPE.chapter]: 'Chapter',
  [ANCHOR_TYPE.instrument]: 'Instrument',
}

export const ANCHOR_MENU_LABELS = {
  [ANCHOR_TYPE.fixedCalendarDate]: 'By exact date — day, day of year, week of year',
  [ANCHOR_TYPE.participantStartDate]: 'Participant start date — default',
  [ANCHOR_TYPE.participantLogin]: 'Participant first login',
  [ANCHOR_TYPE.icfSignatureDate]: 'ICF signature date — offsets ≥ 0',
  [ANCHOR_TYPE.arm]: 'Arm',
  [ANCHOR_TYPE.phase]: 'Phase / period / epoch',
  [ANCHOR_TYPE.visitCycle]: 'Visit cycle (treatment cycle)',
  [ANCHOR_TYPE.visit]: 'Visit — any set of visits, then which date',
  [ANCHOR_TYPE.track]: 'Track',
  [ANCHOR_TYPE.chapter]: 'Chapter',
  [ANCHOR_TYPE.instrument]: 'Instrument — available, completed, missed',
}

/** Figma grouped “Anchored to” menu (SINGLE POINT + ENTITY). */
export const groupedAnchorOptions = [
  {
    label: 'SINGLE POINT',
    options: [ANCHOR_TYPE.fixedCalendarDate, ANCHOR_TYPE.participantStartDate, ANCHOR_TYPE.participantLogin].map(
      key => ({ key, text: ANCHOR_LABELS[key] }),
    ),
  },
  {
    label: 'ENTITY',
    options: ENTITY_ANCHOR_TYPES.map(key => ({ key, text: ANCHOR_LABELS[key] })),
  },
]

/** Visit cycle anchor requires real treatment cycles on the study visit schedule. */
export const buildGroupedAnchorOptions = ({ visitCyclesSelectable = true } = {}) =>
  groupedAnchorOptions.map(group => ({
    ...group,
    options: (group.options || []).map(option =>
      option.key === ANCHOR_TYPE.visitCycle ? { ...option, disabled: !visitCyclesSelectable } : option,
    ),
  }))

export const ENTITY_START_END_ANCHOR_TYPES = [
  ANCHOR_TYPE.arm,
  ANCHOR_TYPE.phase,
  ANCHOR_TYPE.visitCycle,
  ANCHOR_TYPE.track,
]

export const START_OR_END_OPTIONS = [
  { key: START_OR_END.start, text: 'Start' },
  { key: START_OR_END.end, text: 'End' },
]

export const VISIT_STATUS_OPTIONS = [
  { key: VISIT_STATUS.targetDate, text: 'Target date' },
  { key: VISIT_STATUS.appointmentScheduledDate, text: 'Scheduled' },
  { key: VISIT_STATUS.completedDate, text: 'Completed' },
  { key: VISIT_STATUS.missedDate, text: 'Missed' },
]

/** Figma visit anchor UI — Completed / Missed segmented toggle only. */
export const VISIT_STATUS_UI_OPTIONS = [
  { key: VISIT_STATUS.completedDate, text: 'Completed' },
  { key: VISIT_STATUS.missedDate, text: 'Missed' },
]

export const INSTRUMENT_EVENT_OPTIONS = [
  { key: INSTRUMENT_EVENT.available, text: 'When it becomes available' },
  { key: INSTRUMENT_EVENT.completion, text: 'When it is completed' },
  { key: INSTRUMENT_EVENT.missed, text: 'When it is missed' },
]

export const FIXED_DATE_MODE_OPTIONS = [
  { key: FIXED_DATE_MODE.day, text: 'A day' },
  { key: FIXED_DATE_MODE.dayOfYear, text: 'Day of year' },
  { key: FIXED_DATE_MODE.weekOfYear, text: 'Week of year' },
]

export const RESOLVE_TO_OPTIONS = [
  { key: RESOLVE_TO.earliestOf, text: 'Earliest Of' },
  { key: RESOLVE_TO.latestOf, text: 'Latest Of' },
]
